import { Histogram, HistogramBucket, TimeSeriesData, TimeSeriesValueTuple } from '@/core';

export function getMinAndMax(timeSeriesData: TimeSeriesData): { minValue: number; maxValue: number } {
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < timeSeriesData.series.length; i++) {
    const series = timeSeriesData.series[i];
    if (!series) continue;
    for (let j = 0; j < series.values.length; j++) {
      const valueTuple = series.values[j];
      if (!valueTuple) continue;
      const [_, value] = valueTuple;
      if (value !== null) {
        if (value < minValue) minValue = value;
        if (value > maxValue) maxValue = value;
      }
    }
  }

  return { minValue, maxValue };
}

type Bucket = {
  lowerBound: number;
  upperBound: number;
};

/**
 * Generate buckets based on the min and max values from the time series data
 */
export function generateBuckets(timeSeriesData: TimeSeriesData, numBuckets: number): Bucket[] {
  const { minValue, maxValue } = getMinAndMax(timeSeriesData);
  const bucketSize = (maxValue - minValue) / numBuckets;
  const buckets: Bucket[] = [];
  for (let i = 0; i < numBuckets; i++) {
    const lowerBound = i === 0 ? minValue : minValue + i * bucketSize;
    const upperBound = i === numBuckets - 1 ? maxValue : minValue + (i + 1) * bucketSize;
    buckets.push({ lowerBound, upperBound });
  }
  return buckets;
}

/**
 * Convert time series data to heat map
 */
export function convertTimeSeriesDataToHeatMap(
  timeSeriesData: TimeSeriesData,
  numBuckets = 10,
  getTimestampFromTimeseriesValueTuple = (tuple: TimeSeriesValueTuple) => tuple[0]
): Histogram[] {
  const buckets = generateBuckets(timeSeriesData, numBuckets);

  // Create a map of timestamp with an array of values that fall into that time interval
  const timestampToValuesMap: Record<number, number[]> = {};
  for (let i = 0; i < timeSeriesData.series.length; i++) {
    const series = timeSeriesData.series[i];
    if (!series) continue;
    for (let j = 0; j < series.values.length; j++) {
      const valueTuple = series.values[j];
      if (!valueTuple) continue;
      const [_timestamp, value] = valueTuple;
      if (value === null) {
        continue;
      }

      const timestamp = getTimestampFromTimeseriesValueTuple(valueTuple);
      if (!timestampToValuesMap[timestamp]) {
        timestampToValuesMap[timestamp] = [];
      }
      timestampToValuesMap[timestamp]?.push(value);
    }
  }

  const histograms: Histogram[] = [];

  // Loop through each timestamp, and create a histogram with buckets
  const timestamps = Object.keys(timestampToValuesMap);
  for (let i = 0; i < timestamps.length; i++) {
    const histogramBuckets: HistogramBucket[] = [];
    for (let j = 0; j < buckets.length; j++) {
      const bucket = buckets[j];
      if (!bucket) continue;
      histogramBuckets.push({
        ...bucket,
        count: 0,
      });
    }

    // For each timestamp, loop through the values and place them into the appropriate bucket.
    const timestamp = timestamps[i];
    if (timestamp === undefined) continue;
    const values = timestampToValuesMap[Number(timestamp)];
    if (!values) continue;
    for (let j = 0; j < values.length; j++) {
      const value = values[j];
      if (value === undefined) continue;
      for (let k = 0; k < buckets.length; k++) {
        const bucket = buckets[k];
        if (!bucket) continue;
        const { lowerBound, upperBound } = bucket;
        // Upper bounds are inclusive while lower bounds are exclusive.
        // Except for the first bucket where the value could be the min value and the lower bound
        const isValueInBucket = lowerBound < value && value <= upperBound;
        if (isValueInBucket || (k === 0 && lowerBound <= value && value <= upperBound)) {
          const histogramBucket = histogramBuckets[k];
          if (histogramBucket) {
            histogramBucket.count++;
          }
          break;
        }
      }
    }

    histograms.push({
      timestamp: Number(timestamp),
      buckets: histogramBuckets,
    });
  }

  return histograms;
}
